<template>
  <div
    class="pl-[5px] pr-1 pt-0.5 pb-0.5 flex flex-col gap-px overflow-hidden h-full group cursor-pointer"
    @click="showDetailsModal = true"
  >

    <!-- Line 1: name • instrument • start time | status dot -->
    <div class="flex items-center gap-1 min-w-0">
      <div v-if="lesson.lesson_title" class="text-[10px] font-bold text-fg-base dark:text-fg-base-dark leading-tight shrink-0" :title="lesson.lesson_title">{{ shortLessonTitle }}</div>
      <div class="text-[10px] leading-tight truncate min-w-0">
        <template v-if="lesson.instrument">
          <span class="text-fg-secondary dark:text-fg-base-dark">{{ lesson.instrument }}</span>
          <span class="text-fg-faint dark:text-fg-faint-dark">•</span>
        </template>
        <span class="text-fg-secondary dark:text-fg-base-dark">{{ fmtTime(lesson.lesson_time) }}</span>
      </div>
      <div class="ml-auto shrink-0 flex items-center gap-0.5">
        <PaperClipIcon v-if="lesson.homework_status === 'draft' || lesson.homework_status === 'sent'"
                       class="h-2.5 w-2.5"
                       :class="lesson.homework_status === 'sent' ? 'text-green-600 dark:text-green-400' : 'text-amber-500 dark:text-amber-400'" />
        <div class="w-1.5 h-1.5 rounded-full" :style="{ background: statusDotColor(lessonStatus(lesson)) }" />
      </div>
    </div>

    <!-- Line 2: location • category • duration -->
    <div v-if="lesson.location || lesson.category || lesson.duration_minutes"
         class="flex items-center gap-1 min-w-0 text-[9px] leading-tight">
      <div v-if="lesson.location" class="text-fg-secondary dark:text-fg-secondary-dark shrink-0 truncate">{{ lesson.location }}</div>
      <div v-if="lesson.location && lesson.category" class="text-fg-faint dark:text-fg-faint-dark shrink-0">•</div>
      <div v-if="lesson.category" class="text-fg-secondary dark:text-fg-secondary-dark shrink-0">{{ lesson.category }}</div>
      <div v-if="(lesson.location || lesson.category) && lesson.duration_minutes" class="text-fg-faint dark:text-fg-faint-dark shrink-0">•</div>
      <div v-if="lesson.duration_minutes" class="text-fg-secondary dark:text-fg-secondary-dark shrink-0">{{ fmtDuration(lesson.duration_minutes) }}</div>
    </div>

    <!-- Line 3 (admin only): price • billing status • teacher -->
    <div v-if="isAdmin && (priceLabel || billingLabel || lesson.teacher_name)"
         class="flex items-center gap-1 min-w-0 text-[9px] leading-tight">
      <div v-if="priceLabel" class="text-fg-secondary dark:text-fg-secondary-dark shrink-0">{{ priceLabel }}</div>
      <div v-if="priceLabel && billingLabel" class="text-fg-faint dark:text-fg-faint-dark shrink-0">•</div>
      <div v-if="billingLabel" class="text-fg-secondary dark:text-fg-secondary-dark shrink-0">{{ billingLabel }}</div>
      <div v-if="(priceLabel || billingLabel) && lesson.teacher_name" class="text-fg-faint dark:text-fg-faint-dark shrink-0">•</div>
      <div v-if="lesson.teacher_name" class="text-fg-secondary dark:text-fg-secondary-dark font-medium truncate">{{ lesson.teacher_name.split(' ')[0] }}</div>
    </div>

    <!-- Line 4 (line 3 for teachers): student name (contact first name in parens if different) -->
    <div v-if="studentLine" class="flex items-center gap-1 min-w-0 text-[9px] leading-tight">
      <div class="text-fg-muted dark:text-fg-muted-dark truncate">{{ studentLine }}</div>
    </div>

  </div>

  <!-- Details modal -->
  <LessonDetailsModal
    v-if="showDetailsModal"
    :lesson="lesson"
    :isEditable="isEditable"
    @close="showDetailsModal = false"
    @edit="showDetailsModal = false; showEditModal = true"
  />

  <!-- Edit modal -->
  <LessonEditModal
    v-if="showEditModal"
    :lesson="lesson"
    :allow-locked-single-edit="isAdmin"
    @close="showEditModal = false"
    @saved="applyEdit"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { fmtTime, fmtDuration, lessonStatus, statusDotColor, lessonLockTier } from '@/composables/useCalendarData.js'
import { useDemoMode } from '@/composables/useDemoMode'
import LessonEditModal from '@/components/common/LessonEditModal.vue'
import LessonDetailsModal from '@/components/common/LessonDetailsModal.vue'
import { PaperClipIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  lesson: { type: Object, required: true },
})

const emit = defineEmits(['lessons-removed'])

const showDetailsModal = ref(false)
const showEditModal = ref(false)

const effectivePermissions = useDemoMode().effectivePermissionsFor(() => {
  const user = JSON.parse(localStorage.getItem('user') || 'null')
  return user?.permissions
})
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))

// 'paid' locks out everyone (even admins); 'locked' (dispute window closed,
// nothing charged yet) is admin-overridable; 'open' is unrestricted.
const isEditable = computed(() => {
  const tier = lessonLockTier(props.lesson)
  if (tier === 'paid') return false
  if (tier === 'locked') return isAdmin.value
  return true
})

const applyEdit = (updatedLesson) => {
  showEditModal.value = false
  if (!updatedLesson) return
  if (updatedLesson.lesson_time !== undefined) props.lesson.lesson_time = updatedLesson.lesson_time
  if (updatedLesson.lesson_date !== undefined) props.lesson.lesson_date = updatedLesson.lesson_date
  // See components/common/LessonCard.vue's applyEdit for why these matter — dropping
  // them here left the card showing the pre-edit duration/price after a successful save.
  if (updatedLesson.duration_minutes !== undefined) props.lesson.duration_minutes = updatedLesson.duration_minutes
  if (updatedLesson.price_cents !== undefined) props.lesson.price_cents = updatedLesson.price_cents
  if (updatedLesson.location !== undefined) props.lesson.location = updatedLesson.location
  if (updatedLesson.cancelled_lesson_ids?.length) emit('lessons-removed', updatedLesson.cancelled_lesson_ids)
}

const BILLING_LABELS = {
  scheduled:  'Scheduled',
  unreported: 'Unreported',
  pending:    'Pending',
  processing: 'Processing',
  pending_pm: 'Pending PM',
  submitted:  'Submitted',
  succeeded:  'Succeeded',
  failed:     'Failed',
  skipped:    'Skipped',
  waived:     'Waived',
  on_hold:    'On Hold',
  migrated:   'Migrated',
}
const billingLabel = computed(() => {
  const status = props.lesson.billing_status
  return status ? (BILLING_LABELS[status] || status) : null
})

const priceLabel = computed(() => {
  return props.lesson.price_cents != null ? `$${(props.lesson.price_cents / 100).toFixed(2)}` : null
})

const studentLine = computed(() => {
  const studentName = props.lesson.student_name
  if (studentName) {
    const studentFirst = studentName.split(' ')[0]
    const contactFirst = props.lesson.contact_name ? props.lesson.contact_name.split(' ')[0] : null
    return (contactFirst && contactFirst.toLowerCase() !== studentFirst.toLowerCase())
      ? `${studentName} (${contactFirst})`
      : studentName
  }
  const names = (props.lesson.attendance || []).map(s => s.name).filter(Boolean)
  return names.length ? names.join(' • ') : null
})

const shortLessonTitle = computed(() => {
  const title = props.lesson.lesson_title
  if (!title) return ''
  const namePart = title.split(' - ')[0].trim()
  const words = namePart.split(/\s+/).filter(Boolean)
  const short = words.length <= 1
    ? (words[0] || '')
    : `${words[0]} ${words[words.length - 1][0]}`
  return short.length > 10 ? short.slice(0, 10) + '…' : short
})

const isFuture = computed(() => {
  if (!props.lesson.lesson_date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(props.lesson.lesson_date + 'T00:00:00') >= today
})
</script>
